import type { Query, QueryClient } from '@tanstack/react-query'
import { FacilitatorRoutes, GamerRoutes } from '../../../config/routes/routeEnums'
import {
  deriveOrgSwitchShellRole,
  getOrgSwitchQueryRootsForRole,
  isCriticalOrgSwitchQuery,
  type OrgSwitchShellRole
} from './orgSwitchConstants'

/**
 * Query roots required before dismissing the overlay for the current route.
 * Narrower than the full facilitator shell so we wait for what the user is looking at.
 */
export function getOrgSwitchQueryRootsForPath(pathname: string): readonly string[] {
  const normalized = pathname.replace(/\/$/, '') || '/'

  if (normalized.includes(FacilitatorRoutes.TRAINING_SPRINT_MANAGEMENT)) {
    return ['trainingSprints']
  }
  if (normalized.includes(FacilitatorRoutes.DASHBOARD)) {
    return ['facilitatorDashboard']
  }
  if (normalized.includes(FacilitatorRoutes.UPCOMING_ACTIVITIES)) {
    return ['upcoming-activities']
  }
  if (normalized.includes(GamerRoutes.DASHBOARD)) {
    return []
  }
  if (normalized.includes(GamerRoutes.UPCOMING_ACTIVITIES)) {
    return ['upcoming-activities-gamer-list']
  }

  return getOrgSwitchQueryRootsForRole(deriveOrgSwitchShellRole(pathname))
}

export function matchesOrgSwitchQueryForPath(
  queryKey: unknown,
  role: OrgSwitchShellRole,
  targetOrgId: string,
  pathname: string
): boolean {
  if (!isCriticalOrgSwitchQuery(queryKey, role, targetOrgId)) {
    return false
  }
  if (!Array.isArray(queryKey) || queryKey.length === 0 || typeof queryKey[0] !== 'string') {
    return false
  }
  const roots = getOrgSwitchQueryRootsForPath(pathname)
  return (roots as readonly string[]).includes(queryKey[0])
}

export function getOrgSwitchQueriesForPath(
  queryClient: QueryClient,
  role: OrgSwitchShellRole,
  targetOrgId: string,
  pathname: string
): Query[] {
  return queryClient.getQueryCache().findAll({
    predicate: (query) => matchesOrgSwitchQueryForPath(query.queryKey, role, targetOrgId, pathname)
  })
}

/** True when every observed query for this route/org has finished (success or error). */
export function areOrgSwitchQueriesSettled(
  queryClient: QueryClient,
  role: OrgSwitchShellRole,
  targetOrgId: string,
  pathname: string
): boolean {
  const queries = getOrgSwitchQueriesForPath(queryClient, role, targetOrgId, pathname)
  if (queries.length === 0) {
    return false
  }
  return queries.every(
    (query) =>
      query.state.fetchStatus === 'idle' && (query.state.status === 'success' || query.state.status === 'error')
  )
}

/** True when at least one settled query for this route has successful data. */
export function hasOrgSwitchQueriesWithData(
  queryClient: QueryClient,
  role: OrgSwitchShellRole,
  targetOrgId: string,
  pathname: string
): boolean {
  const queries = getOrgSwitchQueriesForPath(queryClient, role, targetOrgId, pathname)
  return queries.some((query) => query.state.status === 'success' && query.state.data !== undefined)
}
